import React, { Component } from 'react';
import styled from 'styled-components';
import { get } from 'lodash';
import theme from '@ui/theme';
import { attachLocale } from '../../../../lib/utils';
import GenericDropdown from './GenericDropdown';
import CategoriesDropdown from './CategoriesDropdown';
import MyZoneDropdown from './MyZoneDropdown';
import MyZoneDropdownEmptyState from './MyZoneDropdownEmptyState';
import {
  ACCOUNT_DROPDOWN,
  CATEGORIES_DROPDOWN,
  DATA_TRACK_TYPE_INTERNAL,
  MY_ZONE_DROPDOWN,
  MOCK_MORE_DROPDOWN,
  SIGN_IN_BUTTON,
} from '../../../../lib/constants';
import { getNavigationCategoriesBarAnalytics } from './navigationCategoriesBarAnalytics';

// *** Button styles to look up css class names ***
const styles = {
  PRIMARY_BUTTON_BLUE: 'primary',
  SECONDARY_BUTTON_WHITE: 'secondary',
  CATEGORIES_DROPDOWN: 'category',
};

const buttonClass = (style) => {
  return get(styles, [style], '');
};

// *** STYLED COMPONENTS ***
const NavDropdownContainer = styled.span`
  ${(p) => (!p.disableRelative ? 'position: relative;' : '')}
  border: none;
  background: none;
`;

const NavSlotAnchor = styled.a`
  ${(p) => p.theme.typographyRebrand.body.md}
  text-align: right;
  color: ${(p) => p.theme.c.text.primary};
  text-decoration: none;
  margin: 0 ${(p) => p.theme.unit(2)};
  padding: ${(p) => p.theme.unit(2)} 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  :hover {
    text-decoration: underline;
  }
  position: ${(p) => (p.hideElement ? 'fixed' : 'relative')};
  ${(p) => (p.hideElement ? 'top: -100px;' : '')}
  &.primary, &.secondary, &.category {
    text-align: center;
    margin: ${(p) => p.theme.unit(1)};
    padding: ${(p) => p.theme.unit(1)} ${(p) => p.theme.unit(2)};
    :hover {
      text-decoration: none;
    }
  }
  &.category {
    padding: ${(p) => p.theme.unit(1)};
    padding-left: ${(p) => p.theme.unit(2)};
  }
`;

const NavSlotButton = styled(NavSlotAnchor.withComponent('button'))`
  border: none;
  background-color: transparent;
  padding-bottom: ${(p) => p.theme.unit(2)};
  :hover {
    text-decoration: none;
  }
`;

const Arrow = () => (
  <svg
    width="24px"
    height="24px"
    viewBox="0 0 24 24"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false">
    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <path
        fill={theme.c.icon.primary}
        d="M14.2,18.67375 L8,12.42275 L14.203,6.21975 C14.496,5.92675 14.97,5.92675 15.263,6.21975 C15.556,6.51275 15.556,6.98775 15.263,7.28075 L10.117,12.42675 L15.266,17.61775 C15.557,17.91175 15.555,18.38675 15.261,18.67775 C15.115,18.82375 14.924,18.89575 14.733,18.89575 C14.54,18.89575 14.347,18.82175 14.2,18.67375 Z"
        transform="translate(11.741441, 12.447875) rotate(-90.000000) translate(-11.741441, -12.447875) " />
    </g>
  </svg>
);

// *** UTILITY COMPONENTS ***
const InlineFlexCenterDiv = styled.div`
  display: inline-flex;
  align-items: center;
`;

const PositionAbsolute = styled.div`
  position: absolute;
  top: ${(p) => (!isNaN(p.top) ? p.theme.unit(p.top) : 'auto')};
  right: ${(p) => (!isNaN(p.right) ? p.theme.unit(p.right) : 'auto')};
  bottom: ${(p) => (!isNaN(p.bottom) ? p.theme.unit(p.bottom) : 'auto')};
  left: ${(p) => (!isNaN(p.left) ? p.theme.unit(p.left) : 'auto')};
  z-index: ${(p) => p.theme.elevation[2]};
  ${(p) => (p.showMenu ? '' : 'display: none;')}
`;

// *** MAIN COMPONENT ***
class CategoriesNavSlot extends Component {
  constructor() {
    super();
    this.state = {
      isMouseInside: false,
    };
    this.mouseEnter = this.mouseEnter.bind(this);
    this.mouseLeave = this.mouseLeave.bind(this);
    this.menuClickHandler = this.menuClickHandler.bind(this);
    this.onBlurHandler = this.onBlurHandler.bind(this);
  }

  onBlurHandler(event) {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      this.setState({ isMouseInside: false });
    }
  }

  menuClickHandler() {
    this.setState({ isMouseInside: !this.state.isMouseInside });
  }

  mouseEnter() {
    this.setState({ isMouseInside: true });
  }

  mouseLeave() {
    this.setState({ isMouseInside: false });
  }

  render() {
    const {
      url,
      analyticsSlug,
      openInNewTab,
      rootCategory,
      boldText,
      buttonStyle,
      firstName,
      collectorNumber,
      navSlotPurpose,
      locale,
      categoriesDropdownLinks,
      userShopsData,
      myZoneDropdownTitle,
      pageStateModifiers = {},
      globalPageConfig,
      hideAnchors,
      descriptiveText,
    } = this.props;

    const emptyFn = () => {};

    let { text } = this.props;
    let { showAsDropdown } = this.props;
    let { dropdownEntries } = this.props;

    const showURL = navSlotPurpose !== SIGN_IN_BUTTON && !showAsDropdown;

    // *** ANALYTICS ***
    const localizedUrl = attachLocale(url, locale);
    const track = getNavigationCategoriesBarAnalytics({ analyticsSlug, url: localizedUrl });

    // *** DROPDOWN OR TEXT ***
    if (navSlotPurpose === ACCOUNT_DROPDOWN) {
      if (firstName && collectorNumber) {
        text = firstName;
      } else {
        showAsDropdown = false;
        dropdownEntries = [];
      }
    }

    // *** Determine if user has favourite shops
    const hasFavShops = userShopsData && userShopsData.length;

    // *** Pick the which sub component to render on the categories nav bar ***
    const DropdownComponent = (props) => {
      if (!showAsDropdown) {
        return null;
      }

      if (navSlotPurpose === MY_ZONE_DROPDOWN) {
        return hasFavShops ? (
          <PositionAbsolute right={0} {...props}>
            <MyZoneDropdown
              dropdownEntries={dropdownEntries}
              locale={locale}
              myZoneDropdownTitle={myZoneDropdownTitle}
              userShopsData={userShopsData}
              pageStateModifiers={pageStateModifiers}
            />
          </PositionAbsolute>
        ) : (
          <PositionAbsolute top={5} right={0} {...props}>
            <MyZoneDropdownEmptyState
              dropdownEntries={dropdownEntries}
              locale={locale} />
          </PositionAbsolute>
        );
      }
      if (navSlotPurpose === CATEGORIES_DROPDOWN) {
        return (
          <PositionAbsolute top={6} left={1} {...props}>
            <CategoriesDropdown
              pageStateModifiers={pageStateModifiers}
              rootCategory={rootCategory}
              locale={locale}
              categoriesDropdownLinks={categoriesDropdownLinks}
              globalPageConfig={globalPageConfig} />
          </PositionAbsolute>
        );
      }
      if (navSlotPurpose === MOCK_MORE_DROPDOWN) {
        return (
          <PositionAbsolute top={6} left={0} {...props}>
            <GenericDropdown
              dropdownEntries={dropdownEntries}
              locale={locale} />
          </PositionAbsolute>
        );
      }
      return null;
    };

    // Sign in button needs to trigger a modal
    const navSlotOnClick = showAsDropdown ? this.menuClickHandler : emptyFn;

    const disableRelative = hasFavShops && (navSlotPurpose === MY_ZONE_DROPDOWN);

    const NavSlotProps = {
      id: track.dataTrackingId,
      'data-track-id': track.dataTrackingId,
      'data-track-click': track.dataTrack,
      'data-track-type': DATA_TRACK_TYPE_INTERNAL,
      href: showURL ? track.url : null,
      target: openInNewTab ? '_blank' : '_self',
      onClick: navSlotOnClick,
      className: buttonClass(buttonStyle),
      'aria-label': descriptiveText,
    };

    if (showAsDropdown) {
      return (
        <InlineFlexCenterDiv>
          <NavDropdownContainer
            onMouseEnter={this.mouseEnter}
            onMouseLeave={this.mouseLeave}
            onBlur={this.onBlurHandler}
            disableRelative={disableRelative}
            hideElement={hideAnchors}>
            <NavSlotButton
              aria-haspopup="true"
              aria-expanded={this.state.isMouseInside}
              {...NavSlotProps}>
              {text}
              <Arrow />
            </NavSlotButton>
            <DropdownComponent showMenu={this.state.isMouseInside} />
          </NavDropdownContainer>
        </InlineFlexCenterDiv>
      );
    }

    return (
      <InlineFlexCenterDiv>
        <NavSlotAnchor
          {...NavSlotProps}>
          {text}
        </NavSlotAnchor>
      </InlineFlexCenterDiv>
    );
  }
}

export default CategoriesNavSlot;
